import { styled } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { useCheckPermission } from '../../shared/Helpers/hooks';
import { parseWallClockTime } from '../../shared/Helpers/timezones';
import { PERMISSIONS } from '../../App/constants';
import { SoapNotesStatuses } from '../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { MBHBillingStatus } from '../../redux/API/ClientAPIHelpers/billingProperties';
import { SessionType } from '../../shared/Helpers/constants';
import { useSelector } from '../../redux/store';
import { AdminTypes } from '../../components/Menu';

export const HeaderWrapper = styled('div')(() => ({
	display: 'flex',
	alignItems: 'center',
	'&>div': {
		width: '50%',
	},
}));

export const EDITABLE_FIELDS_POST_SUBMIT = [
	'locationId',
	'participantResponses',
	'participantOther',
	'isSessionSupervised',
	'supervisedSessionId',
	'date',
	'startTime',
	'endTime',
	'clientTelehealthLocationId',
	'userTelehealthLocationId',
	'attendees',
	'note',
	'callTookPlace',
] as const;

export type EditableFieldPostSubmit = (typeof EDITABLE_FIELDS_POST_SUBMIT)[number];

export const NON_EDITABLE_FIELDS_BCBA_POST_SUBMIT = [
	'date',
	'startTime',
	'endTime',
	'locationId',
	'clientTelehealthLocationId',
	'userTelehealthLocationId',
	'participantResponses',
	'participantOther',
	'isSessionSupervised',
	'supervisedSessionId',
	'peopleInAttendance',
] as const;

export const usePostSubmitEditMode = () => {
	const [params] = useSearchParams();
	const role = useSelector((state) => state.account.role);
	const soapNoteStatus = useSelector((state) => state.soapNote.soapNote?.status?.status);
	const POST_SUBMIT_EDIT = useCheckPermission(PERMISSIONS.SOAPNOTE.POST_SUBMIT_EDIT);

	const isPostSubmitParam = params.get('postSubmit') === 'true';
	const isAdmin = role?.section.id === AdminTypes.admin;
	const isBCBA = role?.section.id === AdminTypes.bcba;
	const isSubmitted = soapNoteStatus === SoapNotesStatuses.submitted;

	const isAdminPostSubmitEditMode = isPostSubmitParam && isAdmin && isSubmitted && POST_SUBMIT_EDIT.permissionGranted;
	const isBcbaPostSubmitEditMode = isBCBA && isSubmitted;
	const isPostSubmitEditMode = isAdminPostSubmitEditMode || isBcbaPostSubmitEditMode;

	const isFieldEditable = (fieldName: string): boolean => {
		if (!isPostSubmitEditMode) return true;

		if (isBcbaPostSubmitEditMode) {
			return !NON_EDITABLE_FIELDS_BCBA_POST_SUBMIT.includes(fieldName as (typeof NON_EDITABLE_FIELDS_BCBA_POST_SUBMIT)[number]);
		}

		return EDITABLE_FIELDS_POST_SUBMIT.includes(fieldName as EditableFieldPostSubmit);
	};

	return {
		isPostSubmitEditMode,
		isBcbaPostSubmitEditMode,
		isFieldEditable,
	};
};

export const useCanEditSOAPNote = () => {
	const role = useSelector((state) => state.account.role);
	const currentUserId = useSelector((state) => state.account.user.id);
	const soapNoteStatus = useSelector((state) => state.soapNote.soapNote?.status?.status);
	const noteUserId = useSelector((state) => state.soapNote.soapNote?.user?.id);
	const sessionTypeId = useSelector((state) => state.session.sessionInfo.sessionType?.type);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const isBCBA = role?.section.id === AdminTypes.bcba;
	const canUserEdit = noteUserId === currentUserId || isAdmin;

	// BCBAs can edit submitted notes they own OR supervise (BT only)
	if (isBCBA && soapNoteStatus === SoapNotesStatuses.submitted &&
		(noteUserId === currentUserId || sessionTypeId === SessionType.BehaviorTreatment)) {
		return true;
	}

	if (
		soapNoteStatus === SoapNotesStatuses.submitted ||
		soapNoteStatus === SoapNotesStatuses.expired48 ||
		soapNoteStatus === SoapNotesStatuses.expiredWeek ||
		!canUserEdit
	) {
		return false;
	}

	return true;
};

export const useSessionBillingStatus = () => {
	const billingStatus = useSelector((state) => state.soapNote.soapNote?.mbhBillingStatus ?? null);

	const requiresConfirmation = billingStatus === MBHBillingStatus.Approved || billingStatus === MBHBillingStatus.Billed;

	return {
		billingStatus,
		requiresConfirmation,
	};
};

/**
 * Universal hook for SOAP note field disabled state.
 * Combines canEdit and post-submit edit mode logic into a single hook.
 * Use getFieldDisabled(fieldName) for ALL fields to ensure correct behavior.
 */
export const useSoapNoteFieldDisabled = () => {
	const canEdit = useCanEditSOAPNote();
	const { isPostSubmitEditMode, isFieldEditable } = usePostSubmitEditMode();

	const getFieldDisabled = (fieldName: string): boolean => {
		if (isPostSubmitEditMode) return !isFieldEditable(fieldName);

		return !canEdit;
	};

	return { getFieldDisabled, isPostSubmitEditMode, canEdit };
};

/**
 * Returns the signature date/time for display. `signedAt` is stored as UTC on the
 * server, so we parse it as UTC and convert to the viewer's local time. Falls back to
 * the submission time when no signature timestamp was captured (older notes), and
 * finally to the session date if neither exists (drafts).
 */
export const useSignatureDate = (): string => {
	const soapNote = useSelector((state) => state.soapNote.soapNote);

	if (soapNote?.signedAt) return dayjs.utc(soapNote.signedAt).local().format('MM/DD/YYYY hh:mm A');

	if (soapNote?.submittedAt) return dayjs.utc(soapNote.submittedAt).local().format('MM/DD/YYYY hh:mm A');

	return dayjs(soapNote?.date).format('MM/DD/YYYY');
};

export const useDurationIncreased = () => {
	const { startTime, endTime } = useSelector((state) => state.session.sessionInfo);
	const originalStartTime = useSelector((state) => state.session.originalStartTime);
	const originalEndTime = useSelector((state) => state.session.originalEndTime);

	if (!startTime || !endTime || !originalStartTime || !originalEndTime) {
		return false;
	}

	const originalDurationMinutes = parseWallClockTime(originalEndTime).diff(parseWallClockTime(originalStartTime), 'minute');
	const currentDurationMinutes = parseWallClockTime(endTime).diff(parseWallClockTime(startTime), 'minute');

	return currentDurationMinutes > originalDurationMinutes;
};
